import { Link } from '@inertiajs/react'
import FdeSelect from '@/frontend/features/Fde/FdeSelect'
import TaskCategoriesManager from '@/frontend/features/Fde/TaskCategoriesManager'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { ProjectTask, FdeCopy } from '@/frontend/features/Fde/types'
import type { ProjectTaskLinkOptions } from '@/frontend/features/Fde/projectTaskTypes'

interface Props {
  projectId: string
  task: ProjectTask | null
  options: ProjectTaskLinkOptions
  copy: FdeCopy
  editing: boolean
  canManage: boolean
  disabled: boolean
  categoryId: string
  parentId: string
  onChange: (field: 'task_category_id' | 'parent_task_id', value: string) => void
}

export default function TaskStructureFields(props: Props) {
  const { copy, task } = props
  return (
    <section className="mt-5 space-y-4 border-t border-border/60 pt-4">
      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground">{copy.management.task_category}</p>
        {props.editing ? (
          <FdeSelect
            value={props.categoryId}
            disabled={props.disabled}
            ariaLabel={copy.management.task_category}
            onValueChange={(value) => props.onChange('task_category_id', value)}
            options={[
              { value: '', label: copy.management.no_category },
              ...(props.options.categories ?? []).map((category) => ({
                value: category.id,
                label: category.name,
              })),
            ]}
          />
        ) : (
          <p className="text-sm">{task?.category?.name || copy.management.no_category}</p>
        )}
      </div>
      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground">{copy.management.parent_task}</p>
        {props.editing ? (
          <FdeSelect
            value={props.parentId}
            disabled={props.disabled}
            ariaLabel={copy.management.parent_task}
            onValueChange={(value) => props.onChange('parent_task_id', value)}
            options={[
              { value: '', label: copy.management.no_parent_task },
              ...props.options.tasks.map((parent) => ({
                value: parent.id,
                label: `${parent.identifier} · ${parent.title}`,
              })),
            ]}
          />
        ) : task?.parent_task ? (
          <Link
            className="block text-sm hover:underline"
            href={fdeProjectPath(
              `${fdeRoutes.projectTasks}/${task.parent_task.id}`,
              props.projectId,
            )}
          >
            {task.parent_task.identifier} · {task.parent_task.title}
          </Link>
        ) : (
          <p className="text-sm text-muted-foreground">{copy.management.no_parent_task}</p>
        )}
      </div>
      {props.canManage && (
        <TaskCategoriesManager
          projectId={props.projectId}
          categories={props.options.categories ?? []}
          copy={copy.management}
        />
      )}
    </section>
  )
}
